import * as React from "react"
import { Box } from "@chakra-ui/react"
import { useQuery } from "react-query"
import { ZonesMapSidebar } from "./ZonesMapSidebar"
import { NAV_WIDTH, TOPBAR_HEIGHT, WORLD_BBOX } from "../lib/config"
import { ZoneType, ZoneEntity } from "../lib/types/zone"
import { useOrgs } from "./providers/OrgProvider"
import {
  Bbox,
  createBboxFromBounds,
  MultiPolygonData,
  getMultiZoneBounds,
  Polygon,
  getZoneBounds,
} from "../lib/helpers/map"
import { Order } from "../lib/types/shared"
import { useMapBBox } from "./providers/MapBBoxProvider"
import ZonesMapMapbox from "./ZonesMapMapbox"
import ZonesMapInfo from "./ZonesMapInfo"
import ZonesMapGoogleMap from "./ZonesMapGoogleMap"
import { useMapSettings } from "./providers/MapSettingsProvider"
import { Loading } from "./Loading"
import { ErrorBoundary } from "./ErrorBoundary"

interface ZonesResponse {
  count: number
  results: ZoneEntity[]
}

interface Props {
  type: ZoneType
  isCreating: boolean
  editableZone: ZoneEntity | null
  setIsCreating: (boolean: boolean) => void
  setEditableZone: (zone: ZoneEntity | null) => void
}

export const ZonesMapContainer = React.memo(function _ZoneMapContainer(props: Props) {
  const { type, isCreating, editableZone } = props
  const { currentOrgs } = useOrgs()
  const [order, setOrder] = React.useState<Order>({
    order: "-",
    orderBy: "modified",
  })
  const [bbox, setBbox] = React.useState<Bbox | undefined>()
  const [selectedZone, setSelectedZone] = React.useState<ZoneEntity | undefined>()
  const { currentBBox } = useMapBBox()

  const { mapProvider } = useMapSettings()

  // Need to store the editable zone data separately to any holes that may be created
  const [currentEditableZoneData, setCurrentEditableZoneData] = React.useState<MultiPolygonData | undefined>()
  const [zonesToBeCreated, setZonesToBeCreated] = React.useState<{ [id: string]: Polygon } | undefined>()

  const orderParams = order.order + order.orderBy
  const currentOrgsUuids = currentOrgs.map(({ uuid }) => uuid)
  const searchFields = "uuid,name,type,created,modified,polygon,organization,high_threshold,low_threshold"
  const url = `zones/?page_size=99&organizations=${currentOrgsUuids.join()}&type=${
    props.type
  }&ordering=${orderParams}&fields=${searchFields}`

  const { data, isLoading, refetch } = useQuery<ZonesResponse, [string, number]>(url)
  const zones = React.useMemo(() => data?.results || [], [data])
  const filteredZones = editableZone ? zones.filter((z) => z.uuid !== editableZone.uuid) : zones

  const zoneBounds = React.useMemo(() => {
    // Make sure the zones exist and the zones have actual coordinates
    if (zones.length > 0 && zones.filter((z) => z.polygon.coordinates.length > 0).length > 0) {
      return getMultiZoneBounds(zones)
    } else {
      return
    }
  }, [zones])

  React.useEffect(() => {
    // Sets bbox based off zones
    if (isLoading) return
    if (currentBBox) {
      return setBbox(currentBBox)
    }
    if (zoneBounds) {
      setBbox(createBboxFromBounds(zoneBounds))
    } else {
      setBbox(WORLD_BBOX)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneBounds, isLoading])

  React.useEffect(() => {
    // If editing, clear previous zone data
    setZonesToBeCreated(undefined)
    if (editableZone && editableZone.polygon.coordinates.length > 0) {
      // Set map bounds to the currently editing zone
      setBbox(createBboxFromBounds(getZoneBounds(editableZone)))
    }
  }, [editableZone])

  React.useEffect(() => {
    // If creating, clear previous zone data
    setZonesToBeCreated(undefined)
    setCurrentEditableZoneData(undefined)
  }, [isCreating])

  const handleDrawCreate = React.useCallback((polygon: { id: string; coords: Polygon }) => {
    // Merge existing zones to be created, overiding any old ones with the same id
    setZonesToBeCreated((cz) => ({ ...cz, [polygon.id]: polygon.coords }))
  }, [])

  const handleDrawUpdate = React.useCallback((data?: MultiPolygonData) => {
    // Simple updates the current editable zone with the new multi polygon data
    setCurrentEditableZoneData(data)
  }, [])

  const updatePolygons = currentEditableZoneData?.[0] ?? []
  const creatingPolygons = zonesToBeCreated ? Object.values(zonesToBeCreated) : []
  const polyData = [...updatePolygons, ...creatingPolygons]

  return (
    <Box
      position="relative"
      style={{
        height: `calc(100vh - ${TOPBAR_HEIGHT})`,
        width: `calc(100vw - ${NAV_WIDTH})`,
      }}
    >
      <ZonesMapSidebar
        onSetBbox={setBbox}
        onSetOrder={setOrder}
        count={data?.count || 0}
        {...props}
        {...{
          polygonsData: [polyData],
          isLoading,
          selectedZone,
          handleSetSelectedZone: setSelectedZone,
          zones,
          order,
          refetch,
        }}
      />

      {!bbox ? (
        <Loading w="100%" h="100%" />
      ) : (
        <ErrorBoundary>
          {mapProvider === "google_maps" ? (
            <ZonesMapGoogleMap
              {...{
                type,
                editableZone,
                isCreating,
                filteredZones,
                bbox,
                handleDrawCreate,
                handleDrawUpdate,
                setSelectedZone,
                selectedZone,
              }}
            />
          ) : (
            <ZonesMapMapbox
              key={type}
              {...{
                type,
                editableZone,
                isCreating,
                filteredZones,
                bbox,
                handleDrawCreate,
                handleDrawUpdate,
                setSelectedZone,
                selectedZone,
              }}
            />
          )}
        </ErrorBoundary>
      )}
      {(isCreating || !!editableZone) && <ZonesMapInfo />}
    </Box>
  )
})
